import type { TFunction } from 'i18next';
import {
  CheckCircleIcon,
  EditIcon,
  ReceiptIcon,
  SplitIcon,
  TrashIcon,
} from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import {
  ExpensePermissionService,
  formatDate,
  isAmountForNow,
  picksSummary,
  queryKeys,
} from '@splitfiesta/core';
import {
  getCurrencyLocale,
  useNamespace,
  useTranslation,
} from '@splitfiesta/i18n';
import { formatCurrencyWithCode } from '@splitfiesta/shared';
import type {
  AggregatedReactionDto,
  ExpenseResponseDto,
  PendingConversionFields,
} from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import { withAuth } from '@/components/auth';
import { LockedAction } from '@/components/common/LockedAction';
import { RouteChangeLink } from '@/components/common/RouteChangeLink';
import { ExpenseCard } from '@/components/expense/ExpenseCard';
import { ExpenseReceiptThumbnail } from '@/components/expenses/ExpenseReceiptThumbnail';
import { ExpenseRecurringRow } from '@/components/expenses/ExpenseRecurringRow';
import { LineItemsSectionContainer } from '@/components/expenses/lineItems/LineItemsSectionContainer';
import { MemberPicksLine } from '@/components/expenses/lineItems/MemberPicksLine';
import { PersonPickLine } from '@/components/expenses/lineItems/PersonPickLine';
import { pickMembersOf } from '@/components/expenses/lineItems/PicksCard';
import { AppLayout } from '@/components/layout/AppLayout';
import { SupporterBadge } from '@/components/pro/SupporterBadge';
import { ExpenseDetailSkeleton } from '@/components/skeleton';
// Shared DTOs are imported through hooks and used implicitly
import { BackButton } from '@/components/ui/BackButton';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';

import {
  useAddCommentMutation,
  useAddReactionMutation,
  useDeleteCommentMutation,
  useDeleteExpenseMutation,
  useExpenseQuery,
  useRemoveReactionMutation,
  useUpdateCommentMutation,
} from '@/hooks/queries/useExpenses';
import { useGroupQuery } from '@/hooks/queries/useGroups';
import { useReactionOptimisticUpdates } from '@/hooks/useReactionOptimisticUpdates';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { handleApiError } from '@/utils/errorUtils';
import { formatExchangeRate } from '@/utils/exchangeRate';
import { toast } from '@/utils/toast';

function ExpenseDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore(state => state.user);
  const { t, i18n } = useTranslation();
  const [loadingReaction] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load required translation namespaces
  const expensesLoaded = useNamespace('expenses');
  const commonLoaded = useNamespace('common');
  const groupsLoaded = useNamespace('groups');
  const errorsLoaded = useNamespace('errors');
  const socialLoaded = useNamespace('social');
  // The claim dialog's discard prompt and stepper reuse copy from `mobile`.
  useNamespace('mobile');

  const groupId = params.id;
  const expenseId = params.expenseId;

  const deleteExpenseMutation = useDeleteExpenseMutation();

  // React Query hooks for data fetching
  // Disable expense query when deletion is in progress to prevent refetch attempts
  const {
    data: fetchedExpense,
    isLoading: expenseLoading,
    error: expenseError,
  } = useExpenseQuery(expenseId ?? '', !isDeleting);
  // While a delete is in flight the query is disabled and the mutation
  // drops the cached expense, so `fetchedExpense` goes undefined for the
  // round-trip. Keep showing the last one we had — the page must not flip
  // to "not found" while (or after) a delete is refused.
  // `heldExpense` is the snapshot taken when the delete started.
  const [heldExpense, setHeldExpense] = useState(fetchedExpense);
  const expense: (ExpenseResponseDto & PendingConversionFields) | undefined =
    fetchedExpense ?? (isDeleting ? heldExpense : undefined);

  const { data: group, isLoading: groupLoading } = useGroupQuery(groupId ?? '');
  const addReactionMutation = useAddReactionMutation(expenseId ?? '');
  const removeReactionMutation = useRemoveReactionMutation(expenseId ?? '');
  const addCommentMutation = useAddCommentMutation(expenseId ?? '');
  const updateCommentMutation = useUpdateCommentMutation();
  const deleteCommentMutation = useDeleteCommentMutation();

  // Optimistic updates hook for reactions
  const { addReactionOptimistically, toggleReactionOptimistically } =
    useReactionOptimisticUpdates({
      currentUserId: user?.id,
      currentUserName: user?.name,
    });

  const loading = expenseLoading || groupLoading;
  const translationsLoading =
    !expensesLoaded ||
    !commonLoaded ||
    !groupsLoaded ||
    !errorsLoaded ||
    !socialLoaded;

  // Only a failed load takes over the page; a failed delete is a toast.
  const loadFailed = Boolean(expenseError);

  // Creator / group admin / owner — the rule the API enforces.
  const canEdit = ExpensePermissionService.canModifyExpense(
    expense,
    user?.id,
    group?.userRole
  );

  // Early return after all hooks have been called
  if (!groupId || !expenseId) {
    return <Navigate to="/groups" replace />;
  }

  const handleDelete = async () => {
    const message = fetchedExpense?.recurringCadence
      ? `${t('expenses:confirmDelete')} ${t('expenses:repeat.deleteKeepsRule')}`
      : t('expenses:confirmDelete');
    if (!confirm(message)) return;

    // Snapshot what is on screen, then disable the expense query BEFORE
    // starting deletion to prevent refetch attempts.
    setHeldExpense(fetchedExpense);
    setIsDeleting(true);

    try {
      await deleteExpenseMutation.mutateAsync({ expenseId, groupId });
      navigate(`/groups/${groupId}`);
    } catch {
      // The failure is reported by the mutation's own toast (every caller
      // gets it). Here: keep the page, re-enable the query.
      setIsDeleting(false);
    }
  };

  const handleAddReaction = (emoji: string) => {
    if (!expense || !expenseId) return;

    // Check if user has already reacted with this emoji
    const existingReaction = expense.aggregatedReactions?.find(
      r => r.emoji === emoji
    );

    if (existingReaction?.hasReacted) {
      // User already reacted with this emoji, remove it instead
      return handleToggleReaction(emoji, true);
    }

    // Optimistic update FIRST - update cache immediately
    const queryKey = queryKeys.expense(expenseId);
    const previousData = queryClient.getQueryData<ExpenseResponseDto>(queryKey);

    queryClient.setQueryData<ExpenseResponseDto>(queryKey, oldData => {
      if (!oldData) return oldData;

      const updatedReactions = addReactionOptimistically(
        oldData.aggregatedReactions || [],
        emoji
      ) as AggregatedReactionDto[];

      return {
        ...oldData,
        aggregatedReactions: updatedReactions,
      };
    });

    // Fire and forget - rollback on error
    addReactionMutation.mutate(emoji, {
      onError: () => {
        queryClient.setQueryData(queryKey, previousData);
      },
    });
  };

  const handleToggleReaction = (emoji: string, hasReacted: boolean) => {
    if (!expenseId) return;

    // Optimistic update FIRST - update cache immediately
    const queryKey = queryKeys.expense(expenseId);
    const previousData = queryClient.getQueryData<ExpenseResponseDto>(queryKey);

    queryClient.setQueryData<ExpenseResponseDto>(queryKey, oldData => {
      if (!oldData) return oldData;

      const updatedReactions = toggleReactionOptimistically(
        oldData.aggregatedReactions || [],
        emoji,
        hasReacted
      ) as AggregatedReactionDto[];

      return {
        ...oldData,
        aggregatedReactions: updatedReactions,
      };
    });

    // Fire and forget - rollback on error
    const mutation = hasReacted ? removeReactionMutation : addReactionMutation;
    mutation.mutate(emoji, {
      onError: () => {
        queryClient.setQueryData(queryKey, previousData);
      },
    });
  };

  const handleSubmitComment = async (text: string) => {
    if (!user?.id || !expense || !expenseId) {
      return;
    }

    const queryKey = queryKeys.expense(expenseId);
    const previousData = queryClient.getQueryData<ExpenseResponseDto>(queryKey);

    // Create optimistic comment
    const optimisticComment = {
      id: `optimistic-${Date.now()}`,
      userId: user.id,
      name: user.name || 'You',
      profileImage: user.profileImage ?? undefined,
      comment: text,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isEdited: false,
      parentCommentId: undefined,
      replies: [],
    };

    // Update cache optimistically
    queryClient.setQueryData<ExpenseResponseDto>(queryKey, old => {
      if (!old) return old;
      return {
        ...old,
        comments: [...(old.comments || []), optimisticComment],
      };
    });

    try {
      await addCommentMutation.mutateAsync(text);
    } catch {
      // Revert on error
      queryClient.setQueryData(queryKey, previousData);
    }
  };

  const handleEditComment = async (commentId: string, text: string) => {
    if (!expenseId) return;

    const queryKey = queryKeys.expense(expenseId);
    const previousData = queryClient.getQueryData<ExpenseResponseDto>(queryKey);

    // Optimistic update - update cache immediately
    queryClient.setQueryData<ExpenseResponseDto>(queryKey, old => {
      if (!old) return old;
      return {
        ...old,
        comments: (old.comments || []).map(c =>
          c.id === commentId
            ? {
                ...c,
                comment: text,
                updatedAt: new Date().toISOString(),
                isEdited: true,
              }
            : c
        ),
      };
    });

    try {
      await updateCommentMutation.mutateAsync({
        commentId,
        text,
        expenseId,
      });
    } catch (error) {
      // Rollback on error
      queryClient.setQueryData(queryKey, previousData);
      toast.error(handleApiError(error, t as TFunction, 'errors'));
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!expenseId) return;

    const queryKey = queryKeys.expense(expenseId);
    const previousData = queryClient.getQueryData<ExpenseResponseDto>(queryKey);

    // Update cache optimistically - remove the comment
    queryClient.setQueryData<ExpenseResponseDto>(queryKey, old => {
      if (!old) return old;
      return {
        ...old,
        comments: (old.comments || []).filter(c => c.id !== commentId),
      };
    });

    try {
      await deleteCommentMutation.mutateAsync({
        commentId,
        expenseId,
      });
    } catch (error) {
      // Revert on error
      queryClient.setQueryData(queryKey, previousData);
      toast.error(handleApiError(error, t as TFunction, 'errors'));
    }
  };

  // Show skeleton while loading
  if (loading || translationsLoading) {
    return (
      <AppLayout>
        <ExpenseDetailSkeleton />
      </AppLayout>
    );
  }

  if (loadFailed || !expense) {
    return (
      <AppLayout>
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="text-center py-12">
            <ReceiptIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('expenses:detail.notFound')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {loadFailed
                ? t('expenses:loadError')
                : t('expenses:detail.notFoundDescription')}
            </p>
            <BackButton variant="outline" fallbackUrl={`/groups/${groupId}`} />
          </div>
        </div>
      </AppLayout>
    );
  }

  // Stored as `custom`; the list's presence is what makes it itemized.
  const isItemized = Boolean(expense.lineItems);
  const asked = isItemized && !!expense.picksRequestedAt;
  const pickMembers = pickMembersOf(group?.members);
  const picksDone = asked && picksSummary(expense, pickMembers).done;

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="shrink-0 mt-1">
              <BackButton
                variant="ghost"
                size="sm"
                className="p-2"
                fallbackUrl={`/groups/${groupId}`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 mb-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  {expense.emoji && (
                    <span className="mr-2">{expense.emoji}</span>
                  )}
                  {expense.title}
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <RouteChangeLink
                  href={`/groups/${groupId}`}
                  className="text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300 transition-colors font-medium"
                >
                  {group?.name || ''}
                </RouteChangeLink>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {(() => {
              const lockedBody = t('expenses:permissions.lockedBody');

              if (!canEdit) {
                return (
                  <>
                    <LockedAction
                      message={lockedBody}
                      className="w-full sm:w-auto"
                      testId="expense-edit-locked"
                    >
                      <EditIcon className="w-4 h-4 mr-2" />
                      {t('common:actions.edit')}
                    </LockedAction>
                    <LockedAction
                      message={lockedBody}
                      className="w-full sm:w-auto"
                      testId="expense-delete-locked"
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      {t('common:actions.delete')}
                    </LockedAction>
                  </>
                );
              }

              return (
                <>
                  <RouteChangeLink
                    href={`/groups/${groupId}/expenses/${expenseId}/edit`}
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<EditIcon className="w-4 h-4" />}
                      className="w-full sm:w-auto"
                    >
                      {t('common:actions.edit')}
                    </Button>
                  </RouteChangeLink>
                  <Button
                    variant="outline"
                    size="sm"
                    leftIcon={<TrashIcon className="w-4 h-4" />}
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="w-full sm:w-auto text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    {isDeleting
                      ? t('common:actions.deleting')
                      : t('common:actions.delete')}
                  </Button>
                </>
              );
            })()}
          </div>
        </div>

        {/* Expense Details - Single Column */}
        <div className="space-y-4">
          {/* Main expense card */}
          <ExpenseCard
            expense={{
              ...expense,
              aggregatedReactions: expense.aggregatedReactions || [],
            }}
            groupName={group?.name}
            onAddReaction={handleAddReaction}
            onToggleReaction={handleToggleReaction}
            onSubmitComment={handleSubmitComment}
            onEditComment={handleEditComment}
            onDeleteComment={handleDeleteComment}
            loadingReaction={loadingReaction}
            variant="full"
            showNotes={true}
            showMultiplePayers={true}
            showPositionIndicator={true}
            showSocialInteractions={true}
            showSupporterBadges
            isClickable={false}
            editHref={
              canEdit
                ? `/groups/${groupId}/expenses/${expenseId}/edit`
                : undefined
            }
            editLockedMessage={
              canEdit ? undefined : t('expenses:permissions.lockedBody')
            }
          />

          {expense.recurringCadence && (
            <ExpenseRecurringRow
              groupId={groupId}
              cadence={expense.recurringCadence}
            />
          )}

          {/* How it's split - Redesigned */}
          <Card className="p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('expenses:detail.splits')}
              </h3>
              {(expense.createdBy === user?.id ||
                group?.userRole === 'admin' ||
                group?.userRole === 'owner') && (
                <RouteChangeLink
                  href={`/groups/${groupId}/expenses/${expenseId}/edit`}
                >
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<EditIcon className="w-4 h-4" />}
                  >
                    {t('common:actions.edit')}
                  </Button>
                </RouteChangeLink>
              )}
            </div>

            <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 rounded-lg mb-4">
              <div className="flex items-center gap-3">
                <SplitIcon className="w-6 h-6 text-orange-500 dark:text-orange-400" />
                <div className="flex-1">
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    {isItemized && t('expenses:splitMethods.lineItems')}
                    {!isItemized &&
                      expense.splitMethod === 'equal' &&
                      t('expenses:splitMethods.equal')}
                    {!isItemized &&
                      expense.splitMethod === 'percentage' &&
                      t('expenses:splitMethods.percentage')}
                    {!isItemized &&
                      expense.splitMethod === 'shares' &&
                      t('expenses:splitMethods.shares')}
                    {!isItemized &&
                      expense.splitMethod === 'custom' &&
                      t('expenses:splitMethods.custom')}
                    {!isItemized &&
                      !expense.splitMethod &&
                      t('expenses:splitMethods.custom')}
                  </p>
                  <p className="text-sm text-orange-700 dark:text-orange-300 mt-0.5">
                    {isItemized && t('expenses:splitMethods.lineItemsHint')}
                    {!isItemized &&
                      expense.splitMethod === 'equal' &&
                      t('expenses:splitMethods.equalDesc')}
                    {!isItemized &&
                      expense.splitMethod === 'percentage' &&
                      t('expenses:splitMethods.percentageDesc')}
                    {!isItemized &&
                      expense.splitMethod === 'shares' &&
                      t('expenses:splitMethods.sharesDesc')}
                    {!isItemized &&
                      expense.splitMethod === 'custom' &&
                      t('expenses:splitMethods.customDesc')}
                    {!isItemized &&
                      !expense.splitMethod &&
                      t('expenses:splitMethods.customDesc')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {[...expense.splits]
                .sort((a, b) => {
                  // Put current user first
                  if (a.userId === user?.id) return -1;
                  if (b.userId === user?.id) return 1;
                  return 0;
                })
                .map((split, index) => {
                  const splitPercentage = (split.amount / expense.amount) * 100;
                  const isCurrentUser = split.userId === user?.id;

                  return (
                    <div
                      key={index}
                      className={`px-3 py-2.5 ${
                        isCurrentUser
                          ? 'bg-orange-50/50 dark:bg-orange-900/10 rounded-lg'
                          : ''
                      }`}
                    >
                      <div
                        className={`flex justify-between gap-2 ${isItemized ? 'items-start' : 'items-center'}`}
                      >
                        <div
                          className={`flex gap-2.5 min-w-0 flex-1 ${isItemized ? 'items-start' : 'items-center'}`}
                        >
                          <Avatar
                            name={split.userName || 'Unknown'}
                            src={split.profileImage}
                            size="sm"
                            className="shrink-0"
                          />
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2 min-w-0 flex-wrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                {resolveUserName(split.userName, t)}
                              </span>
                              {split.isSupporter && (
                                <SupporterBadge className="shrink-0" />
                              )}
                              {isCurrentUser && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                                  {t('expenses:labels.you')}
                                </span>
                              )}
                              {split.isSettled && (
                                <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400 shrink-0" />
                              )}
                            </div>
                            {asked ? (
                              <PersonPickLine
                                expense={expense}
                                userId={split.userId}
                                members={pickMembers}
                                done={picksDone}
                              />
                            ) : (
                              expense.lineItems && (
                                <MemberPicksLine
                                  items={expense.lineItems}
                                  userId={split.userId}
                                />
                              )
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          {isItemized ? null : expense.splitMethod ===
                              'shares' && split.shares !== undefined ? (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {split.shares}{' '}
                              {split.shares === 1
                                ? t('expenses:labels.share')
                                : t('expenses:labels.shares')}{' '}
                              ({splitPercentage.toFixed(0)}%)
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {splitPercentage.toFixed(0)}%
                            </span>
                          )}
                          {asked &&
                          isAmountForNow(
                            expense,
                            split.userId,
                            pickMembers,
                            picksDone
                          ) ? (
                            <span
                              data-testid={`split-for-now-${split.userId}`}
                              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                            >
                              {t('expenses:picks.forNow', {
                                amount: formatCurrencyWithCode(
                                  split.amount,
                                  expense.currency,
                                  getCurrencyLocale(expense.currency)
                                ),
                              })}
                            </span>
                          ) : (
                            <CurrencyDisplay
                              amount={split.amount}
                              currency={expense.currency}
                              className="text-sm font-semibold text-gray-900 dark:text-gray-100"
                            />
                          )}
                        </div>
                      </div>
                      {!isItemized && (
                        <div
                          data-testid="split-progress"
                          className="mt-2 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden"
                        >
                          <div
                            className={`h-full rounded-full transition-all ${
                              isCurrentUser
                                ? 'bg-orange-500 dark:bg-orange-400'
                                : 'bg-gray-300 dark:bg-gray-600'
                            }`}
                            style={{ width: `${splitPercentage}%` }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>

            {/* Summary row */}
            <div className="mt-4 px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  {t('expenses:labels.total')}
                </span>
                <CurrencyDisplay
                  amount={expense.amount}
                  currency={expense.currency}
                  className="text-sm font-bold text-gray-900 dark:text-gray-100"
                />
              </div>
              {expense.pendingConversion === true ? (
                <div
                  data-testid="pending-conversion-hint"
                  className="mt-1 text-right text-sm text-gray-500 dark:text-gray-400"
                >
                  {t('expenses:conversion.offlineQueued', {
                    date: formatDate(expense.date, i18n.language),
                  })}
                </div>
              ) : (
                expense.originalAmount != null &&
                expense.originalCurrency != null &&
                expense.exchangeRate !== undefined &&
                expense.rateDate !== undefined && (
                  <div
                    data-testid="conversion-detail-line"
                    className="mt-1 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm text-gray-500 dark:text-gray-400"
                  >
                    <span>
                      {t(
                        expense.rateSource === 'manual'
                          ? 'expenses:conversion.detailLineManual'
                          : 'expenses:conversion.detailLine',
                        {
                          original: formatCurrencyWithCode(
                            expense.originalAmount,
                            expense.originalCurrency,
                            i18n.language
                          ),
                          rate: formatExchangeRate(
                            expense.exchangeRate,
                            i18n.language
                          ),
                          date: formatDate(expense.rateDate, i18n.language),
                        }
                      )}
                    </span>
                    {expense.rateSource === 'manual' && (
                      <span
                        data-testid="manual-badge"
                        className="text-xs rounded-full px-2 py-0.5 bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                      >
                        {t('expenses:conversion.manualBadge')}
                      </span>
                    )}
                  </div>
                )
              )}
            </div>
          </Card>

          <LineItemsSectionContainer expense={expense} group={group} />

          {/* Receipt */}
          {expense.receipt && (
            <Card className="p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                {t('expenses:detail.receipt')}
              </h3>
              <ExpenseReceiptThumbnail
                thumbnailUrl={expense.receipt}
                originalUrl={expense.receiptOriginal}
                size="lg"
                data-testid="expense-receipt-thumbnail"
              />
            </Card>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default withAuth(ExpenseDetailPage);
